import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { MemberGender, MemberResponse } from '@bm/contracts';
import { MEMBER_GENDER_LABELS } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Field, Input } from './ui';
import { Modal } from './Modal';

/** "Konto bearbeiten" (ProfileMenu.tsx) — a member edits their own
 * Stammdaten (name, gender, birth date, emergency phone) via
 * `PATCH /members/me`. Deliberately only these fields: e-mail, password and
 * App-Rolle live in a separate, admin-only "Kontozugang" area on
 * `MemberDetailPage.tsx` and are never reachable from here — see
 * docs/members.md. */
export function EditAccountModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: mine } = useQuery({
    queryKey: ['profile', 'member-mine'],
    queryFn: () => api<MemberResponse>('/members/me', { orgScoped: true }),
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    gender: '' as MemberGender | '',
    birthDate: '',
    emergencyPhone: '',
  });
  useEffect(() => {
    if (mine) {
      setForm({
        firstName: mine.firstName,
        lastName: mine.lastName,
        gender: mine.gender ?? '',
        birthDate: mine.birthDate ?? '',
        emergencyPhone: mine.emergencyPhone ?? '',
      });
    }
  }, [mine]);

  const save = useMutation({
    mutationFn: () =>
      api<MemberResponse>('/members/me', {
        method: 'PATCH',
        orgScoped: true,
        body: {
          firstName: form.firstName,
          lastName: form.lastName,
          gender: form.gender || null,
          birthDate: form.birthDate || null,
          emergencyPhone: form.emergencyPhone || null,
        },
      }),
    onSuccess: (updated) => {
      qc.setQueryData(['profile', 'member-mine'], updated);
      void qc.invalidateQueries({ queryKey: ['member'] });
      onClose();
    },
  });

  return (
    <Modal title="Konto bearbeiten" subtitle="Deine Stammdaten" icon={<UserCog size={18} />} onClose={onClose}>
      {!mine ? (
        <p className="text-sm text-slate-500">Lädt …</p>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {save.isError && (
            <Alert>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Vorname">
              <Input
                value={form.firstName}
                required
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            </Field>
            <Field label="Nachname">
              <Input
                value={form.lastName}
                required
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Geschlecht (für die Mannschaftsaufstellung)">
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value as MemberGender | '' }))}
            >
              <option value="">Nicht angegeben</option>
              {Object.entries(MEMBER_GENDER_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Geburtstag (optional)">
              <Input
                type="date"
                value={form.birthDate}
                onChange={(e) => setForm((f) => ({ ...f, birthDate: e.target.value }))}
              />
            </Field>
            <Field label="Telefonnummer für Notfälle (optional)">
              <Input
                type="tel"
                value={form.emergencyPhone}
                onChange={(e) => setForm((f) => ({ ...f, emergencyPhone: e.target.value }))}
              />
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Speichert …' : 'Speichern'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
